import {
  isValidElement,
  forwardRef,
  HTMLProps,
  ReactElement,
  ReactNode,
  RefObject,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components';

// Components
import { IconChevronDown, IconChevronUp } from '@u21/tabler-icons';

import { U21Divider } from 'app/shared/u21-ui/components/display/U21Divider';
import {
  U21MenuLayout,
  U21MenuLayoutProps,
} from 'app/shared/u21-ui/components/layout/U21MenuLayout';

import {
  U21MenuItem,
  U21MenuItemProps,
} from 'app/shared/u21-ui/components/display/U21MenuItem';
import {
  U21Button,
  U21ButtonProps,
} from 'app/shared/u21-ui/components/input/U21Button';

// Utils
import { getDOMProps } from 'app/shared/utils/react';

export type U21MenuButtonItems = (U21MenuItemProps | ReactElement | false)[];

export interface U21MenuButtonProps extends Omit<
  HTMLProps<HTMLButtonElement>,
  'ref'
> {
  alignRight?: boolean;
  backdropRef?: RefObject<HTMLDivElement>;
  buttonProps?: Omit<U21ButtonProps, 'children'>;
  children?: ReactNode;
  items: U21MenuButtonItems;
  menuRef?: RefObject<HTMLDivElement>;
  onClose?: U21MenuLayoutProps['onClose'];
  showArrows?: boolean;
  menuLayoutProps?: Omit<
    U21MenuLayoutProps,
    'children' | 'onClose' | 'trigger' | 'triggerRef'
  >;
  stopPropagation?: boolean;
}

const U21MenuButtonBase = forwardRef<HTMLButtonElement, U21MenuButtonProps>(
  (
    {
      alignRight = false,
      backdropRef,
      buttonProps,
      children,
      items,
      menuLayoutProps,
      menuRef,
      onClose,
      showArrows = false,
      stopPropagation,
      ...rest
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const itemsToRender = useMemo(
      () =>
        items.filter((i): i is U21MenuItemProps | ReactElement => Boolean(i)),
      [items],
    );

    if (!itemsToRender.length) {
      return null;
    }

    return (
      <U21MenuLayout
        alignRight={alignRight}
        backdropRef={backdropRef}
        menuRef={menuRef}
        onClose={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
          onClose?.(e);
          setOpen(false);
        }}
        open={open}
        triggerRef={ref}
        trigger={
          <U21Button
            {...(showArrows && {
              endIcon: open ? <IconChevronUp /> : <IconChevronDown />,
            })}
            {...buttonProps}
            onClick={(e) => {
              if (stopPropagation) {
                e.stopPropagation();
              }
              buttonProps?.onClick?.(e);
              setOpen(!open);
            }}
            {...getDOMProps(rest)}
          >
            {children}
          </U21Button>
        }
        {...menuLayoutProps}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
          menuLayoutProps?.onClick?.(e);
        }}
      >
        {itemsToRender.map((i) => {
          if (isValidElement<any>(i)) {
            return i;
          }
          const { key, ...restItem } = i;
          return (
            <U21MenuItem
              key={i.key || (i.text as string)}
              alignRight={alignRight}
              item={restItem}
              onClose={() => setOpen(false)}
            />
          );
        })}
      </U21MenuLayout>
    );
  },
);

const StyledDivider = styled(U21Divider).attrs({ horizontal: true })`
  margin: 8px 0;
`;

export const U21MenuButton = Object.assign(U21MenuButtonBase, {
  Divider: StyledDivider,
});
